<script lang="ts" setup>
import { watch, onBeforeUnmount, ref, computed, nextTick } from "vue";
import { EditorContent, useEditor } from "@tiptap/vue-3";
import StarterKit from "@tiptap/starter-kit";
import { TextStyle } from "@tiptap/extension-text-style";
import { FontFamily } from "@tiptap/extension-font-family";
import Color from "@tiptap/extension-color";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import TextAlign from "@tiptap/extension-text-align";
import BulletList from "@tiptap/extension-bullet-list";
import OrderedList from "@tiptap/extension-ordered-list";
import ListItem from "@tiptap/extension-list-item";
import Link from "@tiptap/extension-link";

import {
  EditorMode,
  InputDataType,
  type InputData,
  type InputDataRaw,
  type InputDataSign,
  type InputDataSignedAt,
  type SignDataRaw,
} from "./interfaces";

import CustomActions from "./CustomActions.vue";
import CustomInput from "./extensions/CustomInput";
import FontSize from "./extensions/FontSize";
import Indent from "./extensions/Indent";
import Sign from "./extensions/Sign";
import CustomCheckbox from "./extensions/CustomCheckbox";

import Toolbar from "./Toolbar.vue";
import HTMLDocumentStylesWrapper from "./components/HTMLDocumentStylesWrapper.vue";

import { hasValue } from "@/Utils/Objects";

interface Props {
  modelValue: string;
  mode?: EditorMode;
  inputDatas?: { [key: string]: InputData };
}

const props = withDefaults(defineProps<Props>(), { mode: EditorMode.EDITOR });
const emit = defineEmits<{
  (e: "update:modelValue", value: string): void;
  (e: "inputChange", value: InputData): void;
}>();

const handleChangeBoolean = (inputData: InputDataRaw<boolean>) => {
  emit("inputChange", { ...inputData, type: InputDataType.CHECKBOX });
};

const handleClickSign = (inputData: SignDataRaw) => {
  emit("inputChange", { ...inputData, type: InputDataType.SIGN });
};

const handleChangeInput = (InputData: InputDataRaw<string>) => {
  emit("inputChange", { ...InputData, type: InputDataType.INPUT });
};

const isEditable = computed(() => props.mode === EditorMode.EDITOR);

const editor = useEditor({
  extensions: [
    StarterKit.configure({
      bulletList: false,
      orderedList: false,
      listItem: false,
      link: false,
    }),
    Link.configure({
      openOnClick: false,
      autolink: true,
      linkOnPaste: true,
      HTMLAttributes: {
        target: "_blank",
        rel: "noopener noreferrer",
      },
    }),
    ListItem,
    OrderedList,
    BulletList,
    TextAlign.configure({
      types: ["heading", "paragraph"], // puedes agregar más si necesitas
    }),
    TextStyle,
    FontFamily,
    FontSize,
    Color.configure({
      types: ["textStyle"],
    }),
    TaskList,
    TaskItem.configure({
      nested: true,
    }),
    Indent,
    CustomInput.configure({ mode: props.mode, onChange: handleChangeInput }),
    Sign.configure({ mode: props.mode, onClick: handleClickSign }),
    CustomCheckbox.configure({ mode: props.mode, onChange: handleChangeBoolean }),
  ],
  content: props.modelValue,
  editable: isEditable.value,
  onUpdate: ({ editor }) => {
    nextTick(updateEditorHeight);
    emit("update:modelValue", editor.getHTML());
  },
  onCreate: ({ editor }) => {
    editor.setEditable(isEditable.value);

    handleSetupInputs();
  },
});

const documentZoom = ref<number>(1);
const editorContentRef = ref<HTMLDivElement>();
const realHeight = ref<string>("100%");

const zoomStyles = computed(() => ({
  transform: `scale(${documentZoom.value})`,
  transformOrigin: "top left",
  width: `calc(100% / ${documentZoom.value})`,
}));

onBeforeUnmount(() => {
  editor.value?.destroy();
});

const handleChangeZoom = (value: number) => {
  documentZoom.value = value;

  nextTick(updateEditorHeight);
};

const updateEditorHeight = () => {
  const wrappers = editorContentRef.value?.getElementsByClassName("html_editor__content");

  if (hasValue(wrappers) && wrappers.length > 0) {
    const currentHeight = (wrappers[0] as HTMLDivElement).offsetHeight as number;

    realHeight.value = `${currentHeight * documentZoom.value}px`;
  }
};

const handleSetupInputs = (toCleanMap?: { [key: string]: InputData }) => {
  if (isEditable.value || !hasValue(props.inputDatas) || Object.keys(props.inputDatas).length === 0) return;

  const { state } = editor.value!;
  let tr = state.tr;

  state.doc.descendants((node, pos) => {
    if (node.type.name === "customCheckbox") {
      const inputData = props.inputDatas![node.attrs.name];
      const cleanData = toCleanMap?.[node.attrs.name];

      if (hasValue(inputData)) {
        tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, checked: inputData.value });
      } else if (hasValue(cleanData)) {
        tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, checked: false });
      }
    } else if (node.type.name === "CustomInput") {
      const inputData = props.inputDatas![node.attrs.name];
      const cleanData = toCleanMap?.[node.attrs.name];

      if (hasValue(inputData)) {
        tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, value: inputData.value });
      } else if (hasValue(cleanData)) {
        tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, value: "" });
      }
    } else if (node.type.name === "SignBlock") {
      let inputData: InputDataSign | InputDataSignedAt | null = null;

      if (props.mode === EditorMode.COMPLETE) {
        inputData = props.inputDatas![node.attrs.name] as InputDataSign | null;
      } else if (props.mode === EditorMode.VIEWER) {
        inputData = props.inputDatas!["signed_at"] as InputDataSignedAt | null;
      }

      const cleanData = toCleanMap?.[node.attrs.name];

      if (hasValue(inputData)) {
        tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, value: inputData.value });
      } else if (hasValue(cleanData)) {
        tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, value: "" });
      }
    }
  });

  editor.value?.view.dispatch(tr);
};

const syncSignBlocksMode = (mode: EditorMode) => {
  if (!hasValue(editor.value)) return;

  const { state } = editor.value;
  let tr = state.tr;
  let hasChanges = false;

  state.doc.descendants((node, pos) => {
    if (node.type.name !== "SignBlock" || node.attrs.mode === mode) return;

    hasChanges = true;
    tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, mode });
  });

  if (hasChanges) {
    editor.value?.view.dispatch(tr);
  }
};

watch(
  () => props.modelValue,
  (newValue) => {
    const currentHTML = editor.value?.getHTML();

    if (editor.value && newValue !== currentHTML) {
      editor.value.commands.setContent(newValue);

      nextTick(updateEditorHeight);
    }
  }
);

watch(
  () => props.mode,
  (newValue, oldValue) => {
    if (editor.value && newValue !== oldValue) {
      editor.value.setEditable(newValue === EditorMode.EDITOR);

      const ext = editor.value.options.extensions.find((ext) => ext.name === "SignBlock");

      if (hasValue(ext)) {
        ext.options.mode = newValue;
      }

      syncSignBlocksMode(newValue);
      handleSetupInputs();
    }
  }
);

watch(
  () => props.inputDatas,
  (newValue, oldValue) => {
    if (!hasValue(newValue) && !hasValue(oldValue)) return;
    else if (!hasValue(newValue)) handleSetupInputs({});
    else {
      const newKeys = Object.keys(newValue);

      const deletedKeys = Object.keys(oldValue ?? {}).filter((key) => !newKeys.includes(key));
      const deletedMap = deletedKeys.reduce((acc: { [key: string]: InputData }, curr) => {
        acc[curr] = oldValue![curr]!;

        return acc;
      }, {});

      handleSetupInputs(deletedMap);
    }
  }
);
</script>

<template>
  <div class="html_editor">
    <div v-if="hasValue(editor) && isEditable" class="html_editor__header">
      <Toolbar
        class="html_editor__header__toolbar"
        :editor="editor"
        :zoom="documentZoom"
        @update:zoom="handleChangeZoom"
      />

      <CustomActions :editor="editor" />
    </div>

    <div v-if="hasValue(editor)" :style="{ height: `${realHeight}` }" ref="editorContentRef">
      <HTMLDocumentStylesWrapper class="html_editor__content" :mode="props.mode" :style="zoomStyles">
        <EditorContent :editor="editor" />
      </HTMLDocumentStylesWrapper>
    </div>
  </div>
</template>

<style scoped lang="scss">
/* Estilos del editor */
.html_editor {
  &__content {
    @include sp.padding(false, 4);
    @include colors.bg(content, lowest);
    @include radius(s);

    transition: transform 0.2s ease;
    transform-origin: top left;
  }

  &__header {
    position: sticky;
    top: 0;
    z-index: 10;

    @include colors.bg(background, base);

    &__toolbar {
      @include sp.padding(false, 5);
      @include colors.bg(content, lowest);
      @include radius(s);

      @media (max-width: $breakpoint-m) {
        @include sp.padding(false, 2);
      }
    }
  }
}
</style>
